import { Chat } from '../model/chat';
import { ChatFile, getChatFileFromFile } from '../model/chat-file';
import { ChatMessage } from '../model/chat-message';
import { ChatMessageRole } from '../model/chat-message-role';
import { DataOrigin } from '../model/data-origin';
import { Shortcut } from '../model/shortcut';

export abstract class ChatUtils {
  public static createAddChatPayload(
    userInput: string,
    dataOrigin: DataOrigin,
    tempFiles: File[],
    shortcut: Shortcut | null = null,
  ): { chat: Chat; shortcut: Shortcut | null; files: File[] } {
    return {
      chat: {
        id: crypto.randomUUID(),
        title: 'Newly generated chat',
        createdAt: new Date(),
        updatedAt: new Date(),
        shortcutType: shortcut?.type,
        files: tempFiles.map(getChatFileFromFile),
        messages: [ChatUtils.createUserMessage(userInput, dataOrigin)],
      },
      shortcut: shortcut ?? null,
      files: tempFiles,
    };
  }

  public static createAddMessagePayload(
    chat: Chat,
    userInput: string,
    dataOrigin: DataOrigin,
    tempFiles: File[],
  ): { chat: Chat; files: File[] } {
    const allFiles: ChatFile[] = [...(chat.files ?? []), ...tempFiles.map(getChatFileFromFile)];
    return {
      chat: {
        ...chat,
        updatedAt: new Date(),
        files: allFiles,
        messages: [...chat.messages, ChatUtils.createUserMessage(userInput, dataOrigin)],
      },
      files: tempFiles,
    };
  }

  public static getLastMessageByRole(chat?: Chat, role: ChatMessageRole = 'user'): ChatMessage | undefined {
    if (!chat) return undefined;
    for (let i = chat.messages.length - 1; i >= 0; i--) {
      if (chat.messages[i].role === role) {
        return chat.messages[i];
      }
    }
    return undefined;
  }

  public static getLastDataOrigin(chat: Chat | undefined, tempFiles: File[]): DataOrigin {
    const lastUserMessage = ChatUtils.getLastMessageByRole(chat);
    if (!lastUserMessage) return DataOrigin.REPOSITORY;
    if (!lastUserMessage.dataOrigin) return lastUserMessage.fromIESEKnowledge ? DataOrigin.REPOSITORY : DataOrigin.NONE;
    const hasFiles = tempFiles.length > 0 || (chat?.files?.length ?? 0) > 0;
    if (lastUserMessage.dataOrigin === DataOrigin.FILES && !hasFiles) {
      return DataOrigin.REPOSITORY;
    }
    return lastUserMessage.dataOrigin;
  }

  public static normalizeMessage(message: string): string | null {
    message = message.trim();
    const stripEmptyLines = message.replace(/^\r\n$|^\r$|^\n$/g, '');
    if (stripEmptyLines.length === 0) return null;
    message = message.replace(/\r\n|\r|\n/g, '<br />');
    if (message.length === 0) return null;

    return message;
  }

  private static createUserMessage(userInput: string, dataOrigin: DataOrigin): ChatMessage {
    return {
      content: userInput.trim(),
      role: 'user',
      dataOrigin,
    };
  }
}
